import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, fetchImageObjectUrl } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Check, X, FileText } from "lucide-react";

const REASON_LABEL = {
    restraining_order: "Restraining / No-Contact Order",
    safety_concern: "Safety Concern",
    former_partner: "Former Partner",
    other: "Other",
};

export default function SafetyReview() {
    const [rows, setRows] = useState(null);
    const load = () => api.get("/admin/safety-exclusions?status=review").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const resolve = async (id, action, matched_user_id) => {
        try {
            await api.post(`/admin/safety-exclusions/${id}/resolve`, { action, matched_user_id });
            toast.success(action === "confirm" ? "Do-Not-Group block created." : "Dismissed.");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
        <div>
            <AdminHeader title="Safety Review" subtitle="Confirm or dismiss possible identity matches for member Do-Not-Group requests." />
            {!rows ? <p className="text-sm text-zinc-500">Loading…</p> : rows.length === 0 ? (
                <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500" data-testid="safety-empty">No pending safety reviews.</div>
            ) : (
                <div className="space-y-4" data-testid="safety-review-list">{rows.map((e) => <ReviewCard key={e.id} e={e} onResolve={resolve} />)}</div>
            )}
        </div>
    );
}

function ReviewCard({ e, onResolve }) {
    const [sel, setSel] = useState(e.candidates?.[0]?.id || "");
    const [photo, setPhoto] = useState(null);
    useEffect(() => {
        if (e.has_photo) fetchImageObjectUrl(`/admin/safety-exclusions/${e.id}/photo`).then(setPhoto).catch(() => {});
    }, [e.id, e.has_photo]);
    const openDoc = async () => {
        try { const url = await fetchImageObjectUrl(`/admin/safety-exclusions/${e.id}/document`); window.open(url, "_blank"); }
        catch { toast.error("Could not open document."); }
    };
    return (
        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid={`safety-card-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-xs font-600 uppercase tracking-wide text-zinc-400">Requested by</p>
                    <p className="font-600">{e.owner?.name || "—"}</p>
                    <p className="text-xs text-zinc-500">{e.owner?.email} · {e.owner?.city}{e.owner?.state ? `, ${e.owner.state}` : ""}</p>
                </div>
                <Badge tone="amber">Possible match</Badge>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                    <p className="text-xs font-600 uppercase tracking-wide text-zinc-400">Person to avoid (entered)</p>
                    <p className="mt-1 font-600">{e.full_name}{e.nickname ? ` (${e.nickname})` : ""}</p>
                    <ul className="mt-1 space-y-0.5 text-sm text-zinc-600">
                        {e.city && <li>City: {e.city}</li>}
                        {(e.approx_age || e.dob) && <li>Age / DOB: {e.approx_age || e.dob}</li>}
                        {e.email && <li>Email: {e.email}</li>}
                        {e.phone && <li>Phone: {e.phone}</li>}
                        <li>Reason: {REASON_LABEL[e.reason] || e.reason}</li>
                    </ul>
                    <div className="mt-2 flex items-center gap-3">
                        {photo && <img src={photo} alt="" className="h-20 w-20 rounded object-cover" data-testid={`safety-photo-${e.id}`} />}
                        {e.has_document && <button onClick={openDoc} data-testid={`safety-doc-${e.id}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"><FileText className="h-4 w-4" /> View document</button>}
                    </div>
                </div>
                <div>
                    <p className="text-xs font-600 uppercase tracking-wide text-zinc-400">Suggested member match</p>
                    <div className="mt-1 space-y-1">
                        {e.candidates?.length ? e.candidates.map((c) => (
                            <label key={c.id} className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm ${sel === c.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}>
                                <input type="radio" name={`cand-${e.id}`} checked={sel === c.id} onChange={() => setSel(c.id)} data-testid={`safety-cand-${c.id}`} />
                                <span><span className="font-600">{c.name}</span> · {c.email} · {c.city}{c.state ? `, ${c.state}` : ""} · <span className="capitalize">{c.verification_status}</span></span>
                            </label>
                        )) : <p className="text-sm text-zinc-500">No suggested candidates.</p>}
                    </div>
                </div>
            </div>
            <div className="mt-4 flex gap-2">
                <button onClick={() => onResolve(e.id, "confirm", sel)} disabled={!sel} data-testid={`safety-confirm-${e.id}`} className="inline-flex items-center gap-1 rounded bg-zinc-900 px-3 py-1.5 text-sm font-600 text-white disabled:opacity-40"><Check className="h-4 w-4" /> Confirm &amp; block</button>
                <button onClick={() => onResolve(e.id, "dismiss")} data-testid={`safety-dismiss-${e.id}`} className="inline-flex items-center gap-1 rounded border border-zinc-300 px-3 py-1.5 text-sm font-600 text-zinc-700 hover:bg-zinc-50"><X className="h-4 w-4" /> Dismiss</button>
            </div>
        </div>
    );
}
